import { Button } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { SchedulePublicBookingLink } from "@/api/scheduling/schedule-public.types";
import { colors } from "@/design-system/theme/colors";
import { isDateAvailable } from "../utils";

interface BookingCalendarProps {
  bookingLink?: SchedulePublicBookingLink;
  selectedDate: Dayjs;
  visibleMonth: Dayjs;
  /**
   * Set of backend-confirmed available dates (YYYY-MM-DD). Once loaded, this is
   * the source of truth for selectable dates.
   */
  availableDates: ReadonlySet<string> | null;
  isDatesLoading?: boolean;
  onMonthChange: (month: Dayjs) => void;
  onDateSelect: (date: Dayjs) => void;
}

const WEEKDAYS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

// Gray highlight used for selectable/available dates (per design spec).
const AVAILABLE_DATE_BG = "#E5E7EB";
const AVAILABLE_DATE_BG_HOVER = "#D8DBE0";

const buildCalendarDays = (month: Dayjs): Dayjs[] => {
  const start = month.startOf("month").startOf("week");
  return Array.from({ length: 42 }, (_, index) => start.add(index, "day"));
};

export const BookingCalendar = ({
  bookingLink,
  selectedDate,
  visibleMonth,
  availableDates,
  isDatesLoading = false,
  onMonthChange,
  onDateSelect,
}: BookingCalendarProps) => {
  const days = buildCalendarDays(visibleMonth);
  const canGoPreviousMonth = visibleMonth
    .startOf("month")
    .isAfter(dayjs().startOf("month"));

  // Selected-date accent uses the workspace brand color when provided.
  const brandColor =
    bookingLink?.workspace?.brandColor?.trim() || colors.primary;
  const isHex6Color = /^#[0-9a-f]{6}$/i.test(brandColor);
  const selectedShadow = isHex6Color
    ? `0 8px 18px ${brandColor}2E`
    : "0 8px 18px rgba(0, 0, 0, 0.15)";

  return (
    <section
      className="flex min-h-[440px] flex-col border-b px-4 py-5 sm:min-h-[470px] sm:px-6 lg:h-full lg:min-h-0 lg:border-b-0 lg:border-r"
      style={{ borderColor: colors.surfaceSubtle }}
    >
      <div className="mb-4 flex items-center justify-between">
        <h2
          className="text-[20px] font-semibold tracking-normal"
          style={{ color: colors.textDarkest }}
        >
          {visibleMonth.format("MMMM")}{" "}
          <span className="font-normal" style={{ color: colors.textMuted }}>
            {visibleMonth.format("YYYY")}
          </span>
        </h2>

        <div className="flex items-center gap-2">
          <Button
            type="text"
            size="small"
            disabled={!canGoPreviousMonth}
            icon={<ChevronLeft size={17} />}
            className="!h-8 !w-8 !rounded-full !p-0 disabled:!opacity-35"
            aria-label="Previous month"
            onClick={() => onMonthChange(visibleMonth.subtract(1, "month"))}
          />
          <Button
            type="text"
            size="small"
            icon={<ChevronRight size={17} />}
            className="!h-8 !w-8 !rounded-full !p-0"
            aria-label="Next month"
            onClick={() => onMonthChange(visibleMonth.add(1, "month"))}
          />
        </div>
      </div>

      <div
        className="mb-3 grid grid-cols-7 text-center text-[12px] font-semibold tracking-[0.1em] sm:text-[13px]"
        style={{ color: colors.primaryDarkest }}
      >
        {WEEKDAYS.map((day) => (
          <div key={day}>{day}</div>
        ))}
      </div>

      <div
        className="grid min-h-0 flex-1 grid-cols-7 grid-rows-6 items-center gap-x-1 gap-y-1 transition-opacity"
        style={{ opacity: isDatesLoading ? 0.55 : 1 }}
        aria-busy={isDatesLoading}
      >
        {days.map((date) => {
          const dateKey = date.format("YYYY-MM-DD");
          const isCurrentMonth = date.month() === visibleMonth.month();
          const isNextMonth = date.isAfter(visibleMonth.endOf("month"), "day");
          const isSpilloverVisible = isCurrentMonth || isNextMonth;
          const isSelected = date.isSame(selectedDate, "day");
          const isAvailable = isDateAvailable(date, availableDates);
          const isToday = date.isSame(dayjs(), "day");
          const shouldShowMonthLabel = date.date() === 1 && isNextMonth;

          const backgroundColor = isSelected
            ? brandColor
            : isAvailable
              ? AVAILABLE_DATE_BG
              : "transparent";

          return (
            <button
              key={dateKey}
              type="button"
              disabled={!isAvailable}
              onClick={() => onDateSelect(date)}
              className="group relative mx-auto flex h-10 w-10 flex-col items-center justify-center rounded-xl text-[14px] font-semibold transition-colors sm:h-11 sm:w-11 sm:text-[15px] lg:h-12 lg:w-12"
              style={{
                color: !isAvailable
                  ? colors.surfaceDarkest
                  : isSelected
                    ? colors.white
                    : colors.textDarkest,
                backgroundColor,
                border: isSelected
                  ? `1px solid ${brandColor}`
                  : "1px solid transparent",
                opacity: isSpilloverVisible ? 1 : 0,
                pointerEvents: isSpilloverVisible ? "auto" : "none",
                cursor: isAvailable ? "pointer" : "not-allowed",
                boxShadow: isSelected ? selectedShadow : "none",
              }}
              onMouseEnter={(event) => {
                if (isAvailable && !isSelected) {
                  event.currentTarget.style.backgroundColor =
                    AVAILABLE_DATE_BG_HOVER;
                }
              }}
              onMouseLeave={(event) => {
                if (isAvailable && !isSelected) {
                  event.currentTarget.style.backgroundColor = AVAILABLE_DATE_BG;
                }
              }}
              aria-label={date.format("dddd, MMMM D, YYYY")}
              aria-pressed={isSelected}
              aria-current={isToday ? "date" : undefined}
            >
              {shouldShowMonthLabel && (
                <span
                  className="absolute left-1.5 top-1 text-[9px] font-semibold uppercase tracking-normal"
                  style={{
                    color: isSelected ? colors.white : colors.primaryDarkest,
                  }}
                >
                  {date.format("MMM")}
                </span>
              )}
              <span>{date.date()}</span>
              {isToday && !isSelected && (
                <span
                  className="absolute bottom-1.5 h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: brandColor }}
                />
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
};
